/**
 * What this server accepts, fetched once and shared.
 *
 * The old `lib/uploads.ts` mirrored the backend's allow-list by hand, with a
 * comment admitting the mirroring was a maintenance cost worth paying. It was
 * worth paying for two formats; it is not worth paying for twenty, and the
 * failure mode is the one this product keeps writing rules against - a picker
 * that offers a format the server refuses, or refuses one the server accepts.
 *
 * So the list comes from `/media/capabilities`, which is built from the same
 * table `resolve_format` validates against and filtered by which decoders the
 * running image actually has.
 *
 * The static list stays as a **fallback for the first paint only**: the file
 * picker needs an `accept` attribute before any request completes, and an
 * empty one would mean "any file". It is deliberately the conservative subset
 * that has always worked, so the worst case is a picker that is too strict for
 * a moment rather than one that promises too much.
 */

import { useCallback } from "react";
import { getMediaCapabilities } from "@/api/client";
import type { FormatCapability, MediaCapabilities } from "@/api/types";
import { useAsync } from "@/lib/useAsync";
import { FALLBACK_PHOTO_EXTENSIONS, FALLBACK_VIDEO_EXTENSIONS } from "./uploads";

export interface CapabilityView {
  /** Every accepted extension, lowercase, no leading dot. */
  extensions: string[];
  /** For a file input's `accept`. A hint to the OS dialog, nothing more. */
  accept: string;
  photoAccept: string;
  videoAccept: string;
  photos: FormatCapability[];
  videos: FormatCapability[];
  /** True once the server has answered; false while the fallback is in use. */
  loaded: boolean;
}

const asAccept = (extensions: readonly string[]): string =>
  extensions.map((extension) => `.${extension}`).join(",");

function view(capabilities: MediaCapabilities | null): CapabilityView {
  if (capabilities === null) {
    const photos = [...FALLBACK_PHOTO_EXTENSIONS];
    const videos = [...FALLBACK_VIDEO_EXTENSIONS];
    return {
      extensions: [...photos, ...videos],
      accept: asAccept([...photos, ...videos]),
      photoAccept: asAccept(photos),
      videoAccept: asAccept(videos),
      photos: [],
      videos: [],
      loaded: false,
    };
  }

  const photos = capabilities.formats.filter((f) => f.media_type === "photo");
  const videos = capabilities.formats.filter((f) => f.media_type === "video");
  const photoExtensions = photos.flatMap((f) => f.extensions);
  const videoExtensions = videos.flatMap((f) => f.extensions);

  return {
    extensions: [...photoExtensions, ...videoExtensions],
    accept: asAccept([...photoExtensions, ...videoExtensions]),
    photoAccept: asAccept(photoExtensions),
    videoAccept: asAccept(videoExtensions),
    photos,
    videos,
    loaded: true,
  };
}

export function useMediaCapabilities(): CapabilityView {
  const fetcher = useCallback(
    (signal: AbortSignal) => getMediaCapabilities({ signal }),
    [],
  );
  const { state } = useAsync(fetcher);
  return view(state.status === "success" ? state.data : null);
}

/**
 * Why a file will not be sent, or null if it looks fine.
 *
 * Client-side only, and **not a security boundary**: anything here can be
 * bypassed with the developer tools open, and none of it is what protects the
 * server - the backend re-derives the extension, sniffs the container and
 * decodes the file. If this drifts, the consequence is a confusing message,
 * never an accepted file that should have been refused.
 */
export function rejectionReason(file: File, capabilities: CapabilityView): string | null {
  const dot = file.name.lastIndexOf(".");
  const extension = dot === -1 ? "" : file.name.slice(dot + 1).toLowerCase();

  if (extension === "") return "Dosyanın uzantısı yok; tür belirlenemiyor.";
  if (file.size === 0) return "Dosya boş.";
  if (capabilities.extensions.includes(extension)) return null;

  // Only meaningful once the real list has arrived: refusing against the
  // fallback would reject formats this server does accept.
  if (!capabilities.loaded) return null;
  return `.${extension} desteklenmiyor. Desteklenen biçimleri içe aktarma ekranından görebilirsiniz.`;
}
